'use client'
import {
  useAddToCart,
  useCartMiniQuery,
} from '@/hooks/react-query/cart/use-cart'
import { usePathname } from '@/i18n/navigation'
import { useTrackEvent } from '@/lib/gtm/track-event'
import { ETrackingEvent, TTrackingEcommerce } from '@/lib/gtm/type'
import { trackPixelEvent } from '@/lib/tracking/track-pixel'
import {
  calculateCampaignPrice,
  extractCampaignSnapshot,
  getDisplayPriceFromCampaignResult,
  isProductExcludedByActiveCampaign,
} from '@/lib/utils/campaign-price'
import {
  useIsBuy1Get1InGiftShopCampaignActive,
  useIsBuy1Get1ValueMatchCampaignActive,
} from '@/lib/utils/campaign/campaign-active'
import { CAMPAIGN_NAMES } from '@/lib/utils/campaign/campaign-names'
import {
  useAllCampaigns,
  useCampaign,
  useCampaignActive,
} from '@/providers/campaign-provider'
import { ProductFormatted, VariantMapped } from '@/types/product'
import { useTranslations } from 'next-intl'
import { createContext, useContext, useEffect, useState } from 'react'

interface ProductCardContextType {
  product: ProductFormatted
  selectedColor: string
  onChangeColorVariant: (variant: TSelectedColorVariant) => void
  selectedColorVariant: TSelectedColorVariant
  handleAddToCart: (variant: VariantMapped) => Promise<void>
  isPendingAddToCart: boolean
  trackingClickProduct: () => void
}

type TSelectedColorVariant = { color: string; variants: VariantMapped[] }

const ProductCardContext = createContext<ProductCardContextType | undefined>(
  undefined
)

export const ProductCardProvider = ({
  children,
  product,
}: {
  children: React.ReactNode
  product: ProductFormatted
}) => {
  // state
  const { mutateAsync: addToCart, isPending: isPendingAddToCart } =
    useAddToCart()

  const trackEvent = useTrackEvent<Partial<TTrackingEcommerce>>()

  const tColorTitle = useTranslations()

  const [selectedColorVariant, setSelectedColorVariant] =
    useState<TSelectedColorVariant>({} as TSelectedColorVariant)

  // Lấy dữ liệu cần thiết để tính campaign price
  const { data: cartMini } = useCartMiniQuery({ enabled: true })
  const { campaigns } = useAllCampaigns()
  const campaignData = useCampaign(CAMPAIGN_NAMES.BUY_MORE_SAVE_MORE)
  const isCampaignActive = useCampaignActive(CAMPAIGN_NAMES.BUY_MORE_SAVE_MORE)
  const pathname = usePathname()
  const isBuy1Get1InGiftShopActive = useIsBuy1Get1InGiftShopCampaignActive()
  const isBuy1Get1ValueMatchActive = useIsBuy1Get1ValueMatchCampaignActive()

  // NOTE: Check if we're on buy-1-get-1-in-gift-shop page và campaign active
  const isBuy1Get1InGiftShopPage =
    pathname?.includes('buy-1-get-1-in-gift-shop') && isBuy1Get1InGiftShopActive

  // func
  const onChangeColorVariant = (variant: TSelectedColorVariant) => {
    setSelectedColorVariant(variant)
  }

  const handleAddToCart = async (variant: VariantMapped) => {
    if (isPendingAddToCart) return

    // Snapshot campaign data tại thời điểm add to cart (trước khi invalidate)
    const campaignSnapshot = extractCampaignSnapshot({
      cartMini,
      campaignData,
      isCampaignActive,
      isBuy1Get1ValueMatchActive,
    })

    // Tính campaign price với snapshot data
    const campaignPriceResult = calculateCampaignPrice({
      regularPrice: product?.regular_price ?? 0,
      comparePrice: product?.compare_price ?? 0,
      ...campaignSnapshot,
      isExcluded: isProductExcludedByActiveCampaign({
        product,
        campaigns,
        regularPrice: product?.regular_price ?? 0,
        maxEligiblePriceForGift: cartMini?.max_eligible_price_for_gift,
      }),
    })

    // Tính display price theo cùng logic với product listing
    const { displayPrice, displayComparePrice } =
      getDisplayPriceFromCampaignResult({
        campaignResult: campaignPriceResult,
        regularPrice: product.regular_price,
        comparePrice: product.compare_price ?? 0,
      })

    await addToCart({
      payload: {
        product_id: variant.product_id,
        quantity: 1,
        options: {
          id: variant.id,
        },
        // Đánh dấu sản phẩm từ buy-1-get-1-in-gift-shop page
        ...(isBuy1Get1InGiftShopPage && {
          itm_source: 'buy-1-get-1-in-gift-shop',
        }),
      },
      toastOptions: {
        productName: product.title || '',
        variantName: `${tColorTitle(selectedColorVariant.color) || ''} / ${variant?.size || ''}`,
        regularPrice: displayPrice,
        comparePrice: displayComparePrice,
        imageUrl: variant.thumbnail || product.default_thumbnail || '',
      },
    })

    trackEvent({
      event: ETrackingEvent.AddToCart,
      ecommerce: {
        currency: 'MYR',
        value: variant.regular_price,
        items: [
          {
            item_id: product.id,
            item_name: product.title,
            price: variant.regular_price,
            quantity: 1,
            item_brand: 'Coolmate',
            href: product.href,
            item_url: product.href,
            item_image: product.default_thumbnail,
            item_variant: selectedColorVariant.color,
            discount: product.sale_percent,
            item_rating: product?.review?.ratingValue || 0,
          },
        ],
      },
    })
    const ids = product.id + selectedColorVariant.color

    trackPixelEvent('AddToCart', {
      content_name: product.title.replace('/\n/', '').trim(),
      content_category: product?.category,
      content_ids: [ids],
      content_type: 'product',
      value: Number(product.regular_price),
      currency: 'MYR',
    })

    trackPixelEvent('InitiateCheckout', {
      content_ids: [ids],
      eventref: '',
    })
  }

  // effect
  useEffect(() => {
    if (product) {
      setSelectedColorVariant({
        color: Object.keys(product.mapped_variants)[0],
        variants:
          product.mapped_variants[Object.keys(product.mapped_variants)[0]],
      })
    }
  }, [product])

  const trackingClickProduct = () => {
    const eventData = {
      event: ETrackingEvent.SelectItem,
      ecommerce: {
        items: [
          {
            item_id: product.id,
            item_name: product.title,
            price: product.regular_price,
            quantity: 1,
            item_brand: 'Coolmate',
            href: product.href,
            discount: product.sale_percent,
            ...(product.category ? { item_category: product.category } : {}),
            item_url: product.href,
            item_image: product.default_thumbnail,
            item_variant: selectedColorVariant.color,
          },
        ],

        product_id: product.id,
        product_name: product.title,
        product_price: product.regular_price,
        product_quantity: 1,
        product_url: product.href,
        product_image: product.default_thumbnail,
        product_variant: selectedColorVariant.color,
        product_discount: product.sale_percent,
        product_rating: product?.review?.ratingValue || 0,
      },
    }

    const isGoodPrice = product?.tags?.some((tag) =>
      ['goodprice', 'Good Price'].includes(tag)
    )

    if (isGoodPrice) {
      trackEvent({
        event: ETrackingEvent.GoodPrice,
      })
    }
    trackEvent(eventData)
  }

  // render
  return (
    <ProductCardContext.Provider
      value={{
        product,
        selectedColor: selectedColorVariant.color,
        onChangeColorVariant,
        selectedColorVariant,
        handleAddToCart,
        isPendingAddToCart,
        trackingClickProduct,
      }}
    >
      {children}
    </ProductCardContext.Provider>
  )
}

export const useProductCardContext = () => {
  const context = useContext(ProductCardContext)
  if (!context) {
    throw new Error(
      'useProductCardContext must be used within a ProductCardProvider'
    )
  }
  return context
}
